import { Loader2 } from 'lucide-react'
import type { ComponentPropsWithRef, ReactNode } from 'react'
import { cn } from '../../lib/cn'
import { type ButtonSize, buttonClasses, type ButtonVariant } from './button-variants'

// React 19 passes `ref` as an ordinary prop, so no forwardRef wrapper is needed.
type ButtonProps = ComponentPropsWithRef<'button'> & {
  variant?: ButtonVariant
  size?: ButtonSize
  loading?: boolean
  /**
   * Announced to screen readers while `loading` is true. The label itself stays
   * visible so the button never changes width mid-interaction.
   */
  loadingLabel?: string
  children: ReactNode
}

export const Button = ({
  variant = 'primary',
  size = 'md',
  loading = false,
  loadingLabel,
  className,
  disabled,
  type = 'button',
  children,
  ...rest
}: ButtonProps) => (
  <button
    type={type}
    className={buttonClasses(variant, size, className)}
    disabled={disabled ?? loading}
    aria-busy={loading || undefined}
    {...rest}
  >
    {loading ? (
      <Loader2
        aria-hidden="true"
        className="size-4 shrink-0 animate-spin motion-reduce:animate-none"
      />
    ) : null}
    {/*
      `inline-flex`, not a bare span. Tailwind's preflight sets `svg { display:
      block }`, so an icon passed as a child of a plain inline span forces a line
      break and the label drops underneath the icon. The button's own flex layout
      does not reach in here, because these children are one element to it.
    */}
    <span className={cn('inline-flex items-center gap-2', loading && 'opacity-90')}>
      {children}
    </span>
    {loading && loadingLabel ? (
      <span role="status" className="sr-only">
        {loadingLabel}
      </span>
    ) : null}
  </button>
)
